import { apiViajerosStats } from '../utils/api';
import { TRACKING_CONFIG, isTrackingEnabled, isEventTypeEnabled } from '../config/tracking.config';

// Interfaz para los datos de tracking
export interface TrackingEvent {
  user_id: string;
  session_id?: string;
  ip_address?: string;
  user_agent?: string;
}

export interface DownloadEventData extends TrackingEvent {
  student_id: string;
  report_type: string;
  file_name?: string;
  file_size?: number;
  download_url?: string;
}

export interface PageViewEventData extends TrackingEvent {
  page_url: string;
  page_title?: string;
  referrer?: string;
  time_on_page?: number;
}

export interface ClickEventData extends TrackingEvent {
  element_id?: string;
  element_class?: string;
  element_text?: string;
  page_url: string;
  click_position_x?: number;
  click_position_y?: number;
}

// Clase principal del servicio de tracking
class TrackingService {
  private eventQueue: any[] = [];
  private isProcessing = false;
  private readonly BATCH_SIZE = TRACKING_CONFIG.BATCH_SIZE;
  private readonly FLUSH_INTERVAL = TRACKING_CONFIG.FLUSH_INTERVAL;
  private readonly MAX_EVENTS_PER_MINUTE = TRACKING_CONFIG.MAX_EVENTS_PER_MINUTE;
  private readonly MAX_USER_BEHAVIOR_PER_MINUTE = 3; // Límite específico para user-behavior
  private eventCount = 0;
  private userBehaviorCount = 0;
  private lastMinute = Date.now();
  
  // Cache en memoria para session_id
  private cachedSessionId: string | null = null;
  private cachedUserId: string | null = null;

  constructor() {
    // Iniciar el procesamiento de eventos en lote
    this.startBatchProcessing();
  }

  // Método para obtener información del usuario actual
  public getCurrentUser(): string | null {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.id || null;
    } catch {
      return null;
    }
  }

  // Método para obtener session ID con cache en memoria
  public getSessionId(): string {
    const currentUserId = this.getCurrentUser();
    
    // Si cambió el usuario, limpiar cache y generar nuevo session_id
    if (this.cachedUserId !== currentUserId) {
      this.clearSessionCache();
      this.cachedUserId = currentUserId;
    }
    
    // Si no hay session_id en cache, generar uno nuevo
    if (!this.cachedSessionId) {
      this.cachedSessionId = this.generateNewSessionId();
    }
    
    return this.cachedSessionId;
  }

  // Generar nuevo session_id y guardarlo
  private generateNewSessionId(): string {
    const newSessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('tracking_session_id', newSessionId);
    sessionStorage.setItem('tracking_user_id', this.cachedUserId || '');
    return newSessionId;
  }

  // Limpiar cache de sesión
  private clearSessionCache(): void {
    this.cachedSessionId = null;
    this.cachedUserId = null;
  }

  // Método para obtener información del navegador
  public getBrowserInfo() {
    return {
      user_agent: navigator.userAgent,
      ip_address: null, // Se puede obtener con un servicio externo si es necesario
    };
  }

  // Método para agregar evento a la cola con throttling
  private addToQueue(event: any) {
    const now = Date.now();
    
    // Resetear contadores cada minuto
    if (now - this.lastMinute > 60000) {
      this.eventCount = 0;
      this.userBehaviorCount = 0;
      this.lastMinute = now;
    }
    
    // Verificar límite específico para user-behavior
    if (event.eventType === 'user_behavior') {
      if (this.userBehaviorCount >= this.MAX_USER_BEHAVIOR_PER_MINUTE) {
        return;
      }
      this.userBehaviorCount++;
    }
    
    // Verificar límite general de eventos por minuto
    if (this.eventCount >= this.MAX_EVENTS_PER_MINUTE) {
      return;
    }
    
    this.eventCount++;
    
    this.eventQueue.push({
      ...event,
      timestamp: new Date().toISOString(),
      session_id: this.getSessionId(),
      ...this.getBrowserInfo(),
    });

    // Si la cola está llena, procesar inmediatamente
    if (this.eventQueue.length >= this.BATCH_SIZE) {
      this.flushQueue();
    }
  }

  // Método para procesar la cola de eventos
  private async flushQueue() {
    if (this.isProcessing || this.eventQueue.length === 0) return;

    this.isProcessing = true;
    const eventsToProcess = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Procesar eventos en paralelo
      const promises = eventsToProcess.map(event => this.sendEvent(event));
      await Promise.allSettled(promises);
    } catch (error) {
      console.error('Error processing tracking events:', error);
      // Re-agregar eventos fallidos al inicio de la cola
      this.eventQueue.unshift(...eventsToProcess);
    } finally {
      this.isProcessing = false;
    }
  }

  // Método para enviar un evento individual
  private async sendEvent(event: any) {
    const userId = this.getCurrentUser();
    if (!userId) {
      console.warn('No user ID available for tracking');
      return;
    }

    try {
      let payload;
      
      // Estructura específica para user-behavior
      if (event.eventType === 'user_behavior') {
        payload = {
          user_id: userId.toString(),
          event_type: event.data.action || 'user_action',
          event_data: {
            button_id: event.data.metadata?.button_id,
            form_id: event.data.metadata?.form_id,
            page_section: event.data.metadata?.page_section,
            ...event.data.metadata
          },
          session_id: event.session_id,
          ip_address: event.ip_address,
          user_agent: event.user_agent
        };
      } else {
        // Estructura estándar para otros eventos (sin event_type)
        payload = {
          user_id: userId.toString(),
          session_id: event.session_id,
          ip_address: event.ip_address,
          user_agent: event.user_agent,
          ...event.data
        };
      }
      const response = await apiViajerosStats.post(
        `${event.endpoint}`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error sending tracking event:', error);
      throw error;
    }
  }

  // Iniciar procesamiento por lotes
  private startBatchProcessing() {
    setInterval(() => {
      this.flushQueue();
    }, this.FLUSH_INTERVAL);
  }

  // Métodos públicos para tracking

  // Tracking de descarga de reportes
  async trackDownload(data: Omit<DownloadEventData, 'user_id'>) {
    if (!isTrackingEnabled() || !isEventTypeEnabled('DOWNLOADS_ENABLED')) {
      return;
    }
    this.addToQueue({
      endpoint: 'download-event/track',
      eventType: 'download',
      data,
    });
  }

  // Tracking de vista de página - Optimizado para fluidez
  async trackPageView(data: Omit<PageViewEventData, 'user_id'>) {
    if (!isTrackingEnabled() || !isEventTypeEnabled('PAGE_VIEWS_ENABLED')) {
      return;
    }
    
    // Envío inmediato para page views (no bloqueante)
    this.sendPageViewImmediately(data);
  }

  // Envío inmediato de page view (no bloqueante)
  private async sendPageViewImmediately(data: any) {
    try {
      const userId = this.getCurrentUser();
      if (!userId) {
        console.warn('No user ID available for page view tracking');
        return;
      }

      const payload = {
        user_id: userId.toString(),
        session_id: this.getSessionId(),
        ip_address: this.getBrowserInfo().ip_address,
        user_agent: this.getBrowserInfo().user_agent,
        ...data
      };

      // Envío no bloqueante - no esperar respuesta
      apiViajerosStats.post('page-view/track', payload).catch((error) => {
        // Silenciar errores para no afectar UX
        console.warn('Page view tracking failed:', error.message);
      });
    } catch (error) {
      // Silenciar errores para no afectar UX
      console.warn('Page view tracking error:', error);
    }
  }

  // Tracking de clic
  async trackClick(data: Omit<ClickEventData, 'user_id'>) {
    if (!isTrackingEnabled() || !isEventTypeEnabled('CLICKS_ENABLED')) {
      return;
    }
    
    this.addToQueue({
      endpoint: 'click-event/track',
      eventType: 'click',
      data,
    });
  }

  // Tracking de comportamiento del usuario
  async trackUserBehavior(data: Omit<TrackingEvent, 'user_id'> & { action: string; metadata?: any }) {
    if (!isTrackingEnabled() || !isEventTypeEnabled('USER_BEHAVIOR_ENABLED')) {
      return;
    }
    
    this.addToQueue({
      endpoint: 'user-behavior/track',
      eventType: 'user_behavior',
      data,
    });
  }

  // Forzar envío inmediato de eventos pendientes
  async flush() {
    await this.flushQueue();
  }

  // Limpiar sesión cuando el usuario se desloguee
  clearSession() {
    this.clearSessionCache();
    sessionStorage.removeItem('tracking_session_id');
    sessionStorage.removeItem('tracking_user_id');
    console.log('🧹 Tracking session cleared - Cache y sessionStorage limpiados');
  }

  // Método para debugging - ver estado actual del session_id
  getSessionDebugInfo() {
    const currentUserId = this.getCurrentUser();
    const storedUserId = sessionStorage.getItem('tracking_user_id');
    const storedSessionId = sessionStorage.getItem('tracking_session_id');
    
    return {
      currentUserId,
      cachedUserId: this.cachedUserId,
      cachedSessionId: this.cachedSessionId,
      storedUserId,
      storedSessionId,
      isUserChanged: this.cachedUserId !== currentUserId,
      hasCachedSession: !!this.cachedSessionId
    };
  }
}

// Instancia singleton del servicio
export const trackingService = new TrackingService();

// Función helper para tracking automático de page views (versión antigua - mantener para compatibilidad)
export const trackPageViewLegacy = (pageUrl: string, pageTitle?: string, referrer?: string) => {
  trackingService.trackPageView({
    page_url: pageUrl,
    page_title: pageTitle,
    referrer: referrer,
  });
};

// Función helper para tracking de clics
export const trackClick = (element: HTMLElement, pageUrl: string, event?: MouseEvent) => {
  trackingService.trackClick({
    element_id: element.id || undefined,
    element_class: element.className || undefined,
    element_text: element.textContent?.trim() || undefined,
    page_url: pageUrl,
    click_position_x: event ? event.clientX : undefined,
    click_position_y: event ? event.clientY : undefined,
  });
};

// Función helper para tracking de descarga de reportes
export const trackReportDownload = (
  studentId: string,
  reportType: string,
  fileName?: string,
  fileSize?: number,
  downloadUrl?: string
) => {
  trackingService.trackDownload({
    student_id: studentId,
    report_type: reportType,
    file_name: fileName,
    file_size: fileSize,
    download_url: downloadUrl,
  });
};

// Función helper para limpiar sesión de tracking
export const clearTrackingSession = () => {
  trackingService.clearSession();
};

// Función helper para debugging del session_id
export const getSessionDebugInfo = () => {
  return trackingService.getSessionDebugInfo();
};

// Función helper para page view tracking (optimizada)
export const trackPageView = (data: {
  page_url: string;
  page_title: string;
  referrer?: string;
  time_on_page?: number;
}) => {
  trackingService.trackPageView(data);
};

// Función helper para limpiar referrer persistente
export const clearTrackingReferrer = () => {
  sessionStorage.removeItem('tracking_previous_page');
  console.log('🧹 Referrer persistente limpiado');
};

export default trackingService;
